import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { useProfileImage, resolveImageUrl } from '../hooks/useProfileImage';
import { authAPI, profileAPI, subscriptionAPI } from '../services';
import { apiCall } from '../../services/apiUtil';
import { sportTypeAPI } from '../../services/api';
import { isNativePlatform, enableNativePush, disableNativePush } from '../../notification/nativePushService';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import ImageCropModal, { ALLOWED_IMAGE_TYPES } from '../../components/common/ImageCropModal';
import { moveImage } from '../../services/imageUploadService';
import { useImagePicker } from '../../hooks/useImagePicker';
import {
  User, Lock, Shield, Save, Eye, EyeOff, Calendar,
  Mail, Phone, Camera, ArrowLeft, Bell, Trash2, AlertTriangle, Menu,
  GraduationCap, Award, Plus, X as XIcon, CreditCard
} from 'lucide-react';

const VALID_TABS = ['personal', 'password', 'notifications', 'security', 'danger', 'coaching', 'subscription'];

const ProfileEditPage = ({ embedded = false, onProfileImageUpdate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateProfile, updateUserField } = useAuth();
  const { theme } = useTheme();
  const tabParam = new URLSearchParams(location.search).get('tab');
  const [activeSection, setActiveSection] = useState(
    VALID_TABS.includes(tabParam) ? tabParam : 'personal'
  );

  // Re-sync when `?tab=` changes without a remount — e.g. navigating from the Me quick-nav
  // panel while already on this route (only the query string changes, so the useState
  // initializer above never re-runs on its own).
  useEffect(() => {
    setActiveSection(VALID_TABS.includes(tabParam) ? tabParam : 'personal');
  }, [tabParam]);

  const [showSectionMenu, setShowSectionMenu] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const { imageUrl: hookImageUrl } = useProfileImage(user?.id, user?.profileImage);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImageForCrop, setSelectedImageForCrop] = useState(null);

  // Form states
  const [personalInfo, setPersonalInfo] = useState({
    firstName: user?.firstName || user?.name?.split(' ')[0] || '',
    lastName: user?.lastName || user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phone: user?.phoneNumber || user?.phone || '',
    dateOfBirth: (user?.dob || user?.dateOfBirth || '').slice(0, 10),
    gender: user?.gender || '',
    userHandle: user?.userHandle || ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [securityQuestions, setSecurityQuestions] = useState({
    question1: user?.securityQuestion1 || '',
    answer1: user?.securityAnswer1 || '',
    question2: user?.securityQuestion2 || '',
    answer2: user?.securityAnswer2 || '',
    question3: user?.securityQuestion3 || '',
    answer3: user?.securityAnswer3 || ''
  });

  const [notificationPreferences, setNotificationPreferences] = useState({
    email: { transactional: true },
    sms: { transactional: true },
    push: { transactional: true }
  });
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [nativePushEnabled, setNativePushEnabled] = useState(false);
  const [testPushLoading, setTestPushLoading] = useState(false);

  // Subscription — current plan + Stripe Customer Portal link. Lazy-loaded on
  // first visit to the tab, same pattern as the coaching profile below.
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  // Coaching profile — bio/experience/certifications/expertise shown on
  // academies' "Meet Our Coaches" section. Lazy-loaded on first visit to the tab.
  const [coachingLoaded, setCoachingLoaded] = useState(false);
  const [coachingLoading, setCoachingLoading] = useState(false);
  const [coachingSaving, setCoachingSaving] = useState(false);
  const [coachingBio, setCoachingBio] = useState('');
  const [coachingYearsExperience, setCoachingYearsExperience] = useState('');
  const [certifications, setCertifications] = useState([]);
  const [expertise, setExpertise] = useState([]);
  const [sportTypes, setSportTypes] = useState([]);
  const [certNameInput, setCertNameInput] = useState('');
  const [certBodyInput, setCertBodyInput] = useState('');
  const [certYearInput, setCertYearInput] = useState('');
  const [expertiseSportId, setExpertiseSportId] = useState('');
  const [expertiseLevelInput, setExpertiseLevelInput] = useState('');

  // Delete account state
  const [deleteAccountPassword, setDeleteAccountPassword] = useState('');
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Update form data when user data becomes available
  useEffect(() => {
    if (user) {
      setPersonalInfo({
        firstName: user?.firstName || user?.name?.split(' ')[0] || '',
        lastName: user?.lastName || user?.name?.split(' ').slice(1).join(' ') || '',
        email: user?.email || '',
        phone: user?.phoneNumber || user?.phone || '',
        dateOfBirth: (user?.dob || user?.dateOfBirth || '').slice(0, 10),
        gender: user?.gender || '',
        userHandle: user?.userHandle || ''
      });

      setSecurityQuestions({
        question1: user?.securityQuestion1 || '',
        answer1: user?.securityAnswer1 || '',
        question2: user?.securityQuestion2 || '',
        answer2: user?.securityAnswer2 || '',
        question3: user?.securityQuestion3 || '',
        answer3: user?.securityAnswer3 || ''
      });
    }
  }, [user]);

  // Sync profile image URL from hook whenever it changes (e.g. after refreshProfile)
  useEffect(() => {
    if (hookImageUrl) {
      setProfileImageUrl(hookImageUrl);
    }
  }, [hookImageUrl]);

  // Load notification preferences
  useEffect(() => {
    const loadNotificationPreferences = async () => {
      if (user?.id) {
        try {
          setLoadingPreferences(true);
          const preferences = await profileAPI.getPreferences(user.id);
          if (preferences?.notificationPreferences) {
            const prefs = preferences.notificationPreferences;
setNotificationPreferences(prefs);
          }
        } catch (error) {
          console.error('Failed to load notification preferences:', error);
        } finally {
          setLoadingPreferences(false);
        }
      }
    };
    loadNotificationPreferences();
  }, [user?.id]);

  // Check native push state — enabled only if permission granted AND token registered in backend
  useEffect(() => {
    if (!isNativePlatform()) return;
    PushNotifications.checkPermissions().then(({ receive }) => {
      const hasToken = !!localStorage.getItem('fcm_token');
      setNativePushEnabled(receive === 'granted' && hasToken);
    }).catch(() => {});
  }, []);

  // Load newsletter subscription status
  useEffect(() => {
    const loadNewsletterStatus = async () => {
      if (user?.email) {
        try {
          const res = await apiCall(`/newsletter/status?email=${encodeURIComponent(user.email)}`);
          setNewsletterSubscribed(res.subscribed === true);
        } catch {
          // Default to false if can't fetch
          setNewsletterSubscribed(false);
        }
      }
    };
    loadNewsletterStatus();
  }, [user?.email]);

  const securityQuestionOptions = [
    "What was the name of your first pet?",
    "What city were you born in?",
    "What was your mother's maiden name?",
    "What was the name of your first school?",
    "What was your favorite childhood nickname?",
    "What was the make of your first car?",
    "What is your favorite sports team?",
    "What was the name of your best friend in high school?",
    "What street did you grow up on?",
    "What was your favorite food as a child?"
  ];

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleSecurityQuestionChange = (field, value) => {
    setSecurityQuestions(prev => ({ ...prev, [field]: value }));
  };

  const { openPicker: openProfileImagePicker, pickerInput: profileImagePickerInput } = useImagePicker({
    accept: 'image/jpeg,image/png,image/gif,image/webp',
    onFiles: (files) => {
      const file = files[0]
      if (!file) return
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP).')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image size must be less than 10MB.')
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        setSelectedImageForCrop(reader.result)
        setCropModalOpen(true)
      }
      reader.readAsDataURL(file)
    },
  });

  const handleCroppedImage = async ({ url: tempUrl }) => {
    const { url: canonicalUrl } = await moveImage(tempUrl, `profile_images/${user.id}/${crypto.randomUUID()}.jpeg`);
    await apiCall(`/user/${user.id}`, { method: 'PATCH', body: JSON.stringify({ profileImage: canonicalUrl }) });
    setProfileImageUrl(resolveImageUrl(canonicalUrl));
    updateUserField({ profileImage: canonicalUrl });
    toast.success('Profile image uploaded successfully!');
    if (onProfileImageUpdate) await onProfileImageUpdate();
  };

  const handleSavePersonalInfo = async () => {
    if (personalInfo.userHandle) {
      if (!/^[A-Z0-9]{6,8}$/i.test(personalInfo.userHandle)) {
        toast.error('Handle must be 6–8 letters and numbers only (e.g. ABC123).');
        return;
      }
    }

    if (personalInfo.dateOfBirth) {
      const dob = new Date(personalInfo.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear() -
        ((today.getMonth() < dob.getMonth() ||
          (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) ? 1 : 0);
      if (age < 13) {
        toast.error('You must be at least 13 years old.');
        return;
      }
    }

    setLoading(true);

    try {
      const updatedUser = {
        firstName: personalInfo.firstName,
        lastName: personalInfo.lastName,
        email: personalInfo.email,
        phoneNumber: personalInfo.phone,
        dob: personalInfo.dateOfBirth || null,
        gender: personalInfo.gender || null,
        userHandle: personalInfo.userHandle ? personalInfo.userHandle.toUpperCase() : undefined
      };

      const result = await updateProfile(updatedUser);
      if (result.success) {
        toast.success('Personal information updated successfully!');
      } else {
        toast.error(result.error || 'Failed to update personal information. Please try again.');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update personal information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeSection !== 'subscription' || subscriptionLoaded || !user?.id) return;

    const loadSubscription = async () => {
      setSubscriptionLoading(true);
      try {
        const result = await subscriptionAPI.getCurrent();
        setSubscription(result);
        setSubscriptionLoaded(true);
      } catch (error) {
        console.error('Failed to load subscription:', error);
        toast.error('Failed to load subscription info.');
      } finally {
        setSubscriptionLoading(false);
      }
    };

    loadSubscription();
  }, [activeSection, subscriptionLoaded, user?.id]);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const result = await subscriptionAPI.getPortalUrl();
      if (result?.url) {
        window.location.href = result.url;
      } else {
        toast.error('Could not open billing portal. Please try again.');
      }
    } catch (error) {
      console.error('Failed to create billing portal session:', error);
      toast.error('Could not open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    if (activeSection !== 'coaching' || coachingLoaded || !user?.id) return;

    const loadCoachingProfile = async () => {
      setCoachingLoading(true);
      try {
        const [profile, sports] = await Promise.all([
          apiCall(`/user/${user.id}/coaching-profile`),
          sportTypeAPI.getAll()
        ]);
        setCoachingBio(profile.coachingBio || '');
        setCoachingYearsExperience(profile.coachingYearsExperience != null ? String(profile.coachingYearsExperience) : '');
        setCertifications(profile.certifications || []);
        setExpertise(profile.expertise || []);
        setSportTypes(Array.isArray(sports) ? sports : []);
        setCoachingLoaded(true);
      } catch (error) {
        console.error('Failed to load coaching profile:', error);
        toast.error('Failed to load coaching profile.');
      } finally {
        setCoachingLoading(false);
      }
    };

    loadCoachingProfile();
  }, [activeSection, coachingLoaded, user?.id]);

  const addCertification = () => {
    const name = certNameInput.trim();
    if (!name) return;
    setCertifications(prev => [...prev, {
      name,
      issuingBody: certBodyInput.trim() || null,
      yearObtained: certYearInput.trim() ? Number(certYearInput.trim()) : null
    }]);
    setCertNameInput('');
    setCertBodyInput('');
    setCertYearInput('');
  };
  const removeCertification = (index) => setCertifications(prev => prev.filter((_, i) => i !== index));

  const addExpertise = () => {
    if (!expertiseSportId) return;
    const sportTypeId = Number(expertiseSportId);
    if (expertise.some(e => Number(e.sportTypeId) === sportTypeId)) {
      toast.error('You already added expertise for this sport.');
      return;
    }
    const sport = sportTypes.find(s => s.id === sportTypeId);
    setExpertise(prev => [...prev, { sportTypeId, sportName: sport?.sportName || null, level: expertiseLevelInput.trim() || null }]);
    setExpertiseSportId('');
    setExpertiseLevelInput('');
  };
  const removeExpertise = (index) => setExpertise(prev => prev.filter((_, i) => i !== index));

  const handleSaveCoachingProfile = async () => {
    setCoachingSaving(true);
    try {
      const payload = {
        coachingBio: coachingBio.trim() || null,
        coachingYearsExperience: coachingYearsExperience.trim() ? Number(coachingYearsExperience.trim()) : null,
        certifications: certifications.map(c => ({ name: c.name, issuingBody: c.issuingBody || null, yearObtained: c.yearObtained ?? null })),
        expertise: expertise.map(e => ({ sportTypeId: e.sportTypeId, level: e.level || null }))
      };
      const result = await apiCall(`/user/${user.id}/coaching-profile`, { method: 'PUT', body: JSON.stringify(payload) });
      setCertifications(result.certifications || []);
      setExpertise(result.expertise || []);
      toast.success('Coaching profile updated!');
    } catch (error) {
      console.error('Coaching profile update error:', error);
      toast.error('Failed to update coaching profile. Please try again.');
    } finally {
      setCoachingSaving(false);
    }
  };

  const handleSavePassword = async () => {
    setLoading(true);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match.');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    try {
      await authAPI.changePassword(user.id, passwordData.currentPassword, passwordData.newPassword);
      toast.success(user.hasPassword ? 'Password updated successfully!' : 'Password set up successfully! You can now sign in with email and password.');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Password change error:', error);
      toast.error(error.message || 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSecurityQuestions = async () => {
    setLoading(true);

    // Validate that all questions and answers are filled
    const questions = [
      { q: securityQuestions.question1, a: securityQuestions.answer1 },
      { q: securityQuestions.question2, a: securityQuestions.answer2 },
      { q: securityQuestions.question3, a: securityQuestions.answer3 }
    ];

    for (let i = 0; i < questions.length; i++) {
      if (!questions[i].q || !questions[i].a.trim()) {
        toast.error(`Please complete security question ${i + 1}.`);
        setLoading(false);
        return;
      }
    }

    // Check for duplicate questions
    const questionTexts = [securityQuestions.question1, securityQuestions.question2, securityQuestions.question3];
    if (new Set(questionTexts).size !== questionTexts.length) {
      toast.error('Please select different security questions.');
      setLoading(false);
      return;
    }

    try {
      // Call the API to update security questions
      await profileAPI.updateSecurityQuestions(user.id, {
        securityQuestion1: securityQuestions.question1,
        securityAnswer1: securityQuestions.answer1,
        securityQuestion2: securityQuestions.question2,
        securityAnswer2: securityQuestions.answer2,
        securityQuestion3: securityQuestions.question3,
        securityAnswer3: securityQuestions.answer3
      });
      toast.success('Security questions updated successfully!');
    } catch (error) {
      console.error('Security questions update error:', error);
      toast.error(error.message || 'Failed to update security questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationToggle = (channel, type) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [type]: !prev[channel][type]
      }
    }));
  };

  const handleSaveNotificationPreferences = async () => {
    setLoading(true);

    try {
      await profileAPI.updatePreferences(user.id, {
        notificationPreferences: notificationPreferences
      });

      toast.success('Notification preferences updated successfully!');
    } catch (error) {
      console.error('Notification preferences update error:', error);
      toast.error(error.message || 'Failed to update notification preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNativePushToggle = async () => {
    if (nativePushEnabled) {
      try {
        await disableNativePush();
        setNativePushEnabled(false);
        const updatedPrefs = { ...notificationPreferences, push: { ...notificationPreferences.push, transactional: false } };
        setNotificationPreferences(updatedPrefs);
        await profileAPI.updatePreferences(user.id, { notificationPreferences: updatedPrefs });
        toast.success('Push notifications disabled');
      } catch (err) {
        toast.error('Failed to disable push notifications');
      }
    } else {
      try {
        const enabled = await enableNativePush();
        if (enabled) {
          setNativePushEnabled(true);
          const updatedPrefs = { ...notificationPreferences, push: { ...notificationPreferences.push, transactional: true } };
          setNotificationPreferences(updatedPrefs);
          await profileAPI.updatePreferences(user.id, { notificationPreferences: updatedPrefs });
          toast.success('Push notifications enabled');
          // Send a test push so the user can confirm delivery is working
          apiCall('/notifications/send', {
            method: 'POST',
            body: JSON.stringify({
              notification_id: 'PUSH_TEST',
              user_id: user.id,
              created_by: user.id,
              data: { first_name: user.firstName || user.first_name || '', last_name: user.lastName || user.last_name || '' }
            })
          }).catch(() => {});
        } else if (!localStorage.getItem('fcm_token_device')) {
          toast.error('Push token not available yet — please restart the app and try again');
        } else {
          toast('Please enable notifications in iPhone Settings → Kynaix Sports → Notifications', { duration: 5000 });
        }
      } catch (err) {
        toast.error('Failed to enable push notifications');
      }
    }
  };

  const handleTestPush = async () => {
    setTestPushLoading(true);
    try {
      await apiCall('/notifications/send', {
        method: 'POST',
        body: JSON.stringify({
          notification_id: 'PUSH_TEST',
          user_id: user.id,
          created_by: user.id,
          data: { first_name: user.firstName || user.first_name || '', last_name: user.lastName || user.last_name || '' }
        })
      });
      toast.success('Test notification sent!');
    } catch (err) {
      toast.error('Failed to send test notification');
    } finally {
      setTestPushLoading(false);
    }
  };

  const handleNewsletterToggle = async () => {
    const enabling = !newsletterSubscribed;
    try {
      if (enabling) {
        await apiCall('/newsletter/subscribe', {
          method: 'POST',
          body: JSON.stringify({ email: user.email, source: 'profile' })
        });
        setNewsletterSubscribed(true);
        toast.success('Subscribed to newsletter');
      } else {
        await apiCall('/newsletter/unsubscribe/direct', { method: 'POST' });
        setNewsletterSubscribed(false);
        toast.success('Unsubscribed from newsletter');
      }
    } catch (error) {
      toast.error('Failed to update newsletter subscription.');
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteAccountPassword) {
      toast.error('Please enter your password to confirm account deletion.');
      return;
    }

    setDeletingAccount(true);

    try {
      await profileAPI.deleteAccount(user.id, deleteAccountPassword);
      // Clear all auth data and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('socialUser');
      window.location.href = '/signin?deleted=true';
    } catch (error) {
      console.error('Delete account error:', error);
      toast.error(error.message || 'Failed to delete account. Please check your password and try again.');
      setDeletingAccount(false);
    }
  };

  const sections = [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'password', label: 'Change Password', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security Questions', icon: Shield },
    { id: 'coaching', label: 'Coaching Profile', icon: GraduationCap },
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
    { id: 'danger', label: 'Delete Account', icon: Trash2 }
  ];

  return (
    <div className={`${embedded ? '' : 'min-h-screen py-6 px-4 sm:px-6 lg:px-8'} ${embedded ? '' : theme.background}`}>
      <div className={`${embedded ? '' : 'max-w-4xl mx-auto'}`}>
        {/* Header - Only show when not embedded */}
        {!embedded && (
          <div className="mb-8">
            <button
              onClick={() => navigate('/profile')}
              className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Profile
            </button>
            <h1 className={`text-3xl font-bold ${theme.text}`}>Manage Profile</h1>
            <p className={`${theme.textMuted} mt-2`}>Update your personal information, security settings, and preferences.</p>
          </div>
        )}


        {/* Section Navigation - Horizontal Tabs */}
        <div className="mb-6">
          {/* Desktop: Show all section tabs */}
          <div className="hidden md:flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeSection === section.id
                    ? section.id === 'danger'
                      ? 'bg-red-500 text-white'
                      : 'bg-blue-500 text-white'
                    : `${theme.backgroundSecondary} ${theme.text} ${theme.cardHover}`
                }`}
              >
                <section.icon className="w-4 h-4" />
                {section.label}
              </button>
            ))}
          </div>

          {/* Mobile: Show dropdown menu */}
          <div className="md:hidden relative">
            <div className="flex gap-2 pb-2">
              {/* Section Menu Button */}
              <button
                onClick={() => setShowSectionMenu(!showSectionMenu)}
                className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover} flex items-center gap-2`}
              >
                <Menu className="w-4 h-4" />
                Settings
              </button>

              {/* Quick access - show current section */}
              <div className="flex overflow-x-auto gap-2 scrollbar-hide">
                <button
                  className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
                    activeSection === 'danger'
                      ? 'bg-red-500 text-white'
                      : 'bg-blue-500 text-white'
                  }`}
                >
                  {sections.find(s => s.id === activeSection)?.icon &&
                    React.createElement(sections.find(s => s.id === activeSection).icon, { className: "w-4 h-4" })
                  }
                  {sections.find(s => s.id === activeSection)?.label}
                </button>
              </div>
            </div>

            {/* Dropdown Menu */}
            {showSectionMenu && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 bg-black/50 z-40"
                  onClick={() => setShowSectionMenu(false)}
                />

                {/* Dropdown Content */}
                <div className={`absolute top-full left-0 right-0 mt-2 ${theme.card} rounded-xl shadow-xl z-50 overflow-hidden border ${theme.border}`}>
                  <div className="py-2">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => { setActiveSection(section.id); setShowSectionMenu(false); }}
                        className={`w-full px-4 py-3 text-left ${theme.text} ${theme.cardHover} transition-colors flex items-center gap-3 ${
                          section.id === 'danger' ? 'text-red-600' : ''
                        }`}
                      >
                        <section.icon className="w-4 h-4" />
                        {section.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div>
          <div className={`${theme.card} rounded-xl ${theme.shadow}`}>
              {/* Personal Information Section */}
              {activeSection === 'personal' && (
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <User className="w-6 h-6 text-blue-600 mr-3" />
                    <h2 className={`text-xl font-semibold ${theme.text}`}>Personal Information</h2>
                  </div>

                  <div className="space-y-6">
                    {/* Profile Picture */}
                    <div className="flex items-center gap-x-6">
                      <div className="relative">
                        {profileImageUrl ? (
                          <img
                            key={profileImageUrl}
                            src={profileImageUrl}
                            alt="Profile"
                            className="w-24 h-24 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-3xl font-bold text-white">
                              {user?.firstName?.charAt(0)?.toUpperCase() || user?.name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                        )}
                        <button
                          onClick={openProfileImagePicker}
                          disabled={uploadingImage}
                          className="absolute bottom-0 right-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {uploadingImage ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Camera className="w-4 h-4" />
                          )}
                        </button>
                        {profileImagePickerInput}
                      </div>
                      <div>
                        <h3 className={`font-medium ${theme.text}`}>Profile Photo</h3>
                        <p className={`text-sm ${theme.textMuted}`}>
                          {uploadingImage ? 'Uploading...' : 'Click camera icon to upload a new profile picture'}
                        </p>
                        <p className={`text-xs ${theme.textMuted} mt-1`}>Max size: 10MB (JPEG, PNG, GIF, WebP)</p>
                      </div>
                    </div>

                    {/* Basic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>First Name</label>
                        <input
                          type="text"
                          value={personalInfo.firstName}
                          onChange={(e) => handlePersonalInfoChange('firstName', e.target.value)}
                          className={`w-full px-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>Last Name</label>
                        <input
                          type="text"
                          value={personalInfo.lastName}
                          onChange={(e) => handlePersonalInfoChange('lastName', e.target.value)}
                          className={`w-full px-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>Email</label>
                        <div className="relative">
                          <Mail className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="email"
                            value={personalInfo.email}
                            onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                          />
                        </div>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>Phone</label>
                        <div className="relative">
                          <Phone className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="tel"
                            value={personalInfo.phone}
                            onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>
                      <div className="min-w-0">
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>Date of Birth</label>
                        <div className="relative overflow-hidden">
                          <Calendar className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hidden md:block" />
                          <input
                            type="date"
                            value={personalInfo.dateOfBirth}
                            onChange={(e) => handlePersonalInfoChange('dateOfBirth', e.target.value)}
                            className={`w-full px-4 md:pl-10 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                          />
                        </div>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>Gender</label>
                        <select
                          value={personalInfo.gender}
                          onChange={(e) => handlePersonalInfoChange('gender', e.target.value)}
                          className={`w-full px-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                          <option value="prefer-not-to-say">Prefer not to say</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>Player Handle</label>
                        <input
                          type="text"
                          value={personalInfo.userHandle}
                          onChange={(e) => handlePersonalInfoChange('userHandle', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                          maxLength={8}
                          placeholder="e.g. ABC123"
                          className={`w-full px-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text} font-mono tracking-widest`}
                        />
                        <p className={`text-xs ${theme.textMuted} mt-1`}>
                          6–8 uppercase letters and numbers. Used in your public profile URL: <span className="font-mono">/player/{personalInfo.userHandle || 'ABC123'}</span>
                        </p>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-6 border-t">
                      <button
                        onClick={handleSavePersonalInfo}
                        disabled={loading}
                        className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed flex items-center`}
                      >
                        <Save className="w-5 h-5 mr-2" />
                        {loading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Password Section */}
              {activeSection === 'password' && (
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <Lock className="w-6 h-6 text-blue-600 mr-3" />
                    <h2 className={`text-xl font-semibold ${theme.text}`}>Change Password</h2>
                  </div>

                  <div className="max-w-md space-y-6">
                    {!user.hasPassword && (
                      <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
                        You signed in with Google. Set a password below to also enable email &amp; password login.
                      </div>
                    )}

                    {user.hasPassword && (
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>Current Password</label>
                        <div className="relative">
                          <input
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={passwordData.currentPassword}
                            onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                            className={`w-full px-4 py-3 pr-12 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                            placeholder="Enter current password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-2`}>New Password</label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={passwordData.newPassword}
                          onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                          className={`w-full px-4 py-3 pr-12 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-2`}>Confirm New Password</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={passwordData.confirmPassword}
                          onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                          className={`w-full px-4 py-3 pr-12 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                          placeholder="Confirm new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="pt-4">
                      <h4 className={`font-medium ${theme.text} mb-2`}>Password Requirements:</h4>
                      <ul className={`text-sm ${theme.textMuted} space-y-1`}>
                        <li>• At least 6 characters long</li>
                        <li>• Should contain uppercase and lowercase letters</li>
                        <li>• Should include numbers and special characters</li>
                        <li>• Should not be easily guessable</li>
                      </ul>
                    </div>

                    <div className="flex justify-end pt-6 border-t">
                      <button
                        onClick={handleSavePassword}
                        disabled={loading || (user.hasPassword && !passwordData.currentPassword) || !passwordData.newPassword || !passwordData.confirmPassword}
                        className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed flex items-center`}
                      >
                        <Save className="w-5 h-5 mr-2" />
                        {loading ? (user.hasPassword ? 'Updating...' : 'Setting up...') : (user.hasPassword ? 'Update Password' : 'Set Password')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Notification Preferences Section */}
              {activeSection === 'notifications' && (
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <Bell className="w-6 h-6 text-blue-600 mr-3" />
                    <h2 className={`text-xl font-semibold ${theme.text}`}>Notification Preferences</h2>
                  </div>

                  <p className={`${theme.textMuted} mb-6`}>
                    Control how you receive notifications. You can opt out of marketing communications while still receiving important transactional updates.
                  </p>

                  {loadingPreferences ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                    <div className="space-y-8">
                      {/* Email Notifications */}
                      <div className={`p-4 rounded-lg border ${theme.border}`}>
                        <div className="flex items-center mb-4">
                          <Mail className="w-5 h-5 text-blue-600 mr-2" />
                          <h3 className={`font-medium ${theme.text}`}>Email Notifications</h3>
                        </div>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium ${theme.text}`}>Transactional Emails</p>
                              <p className={`text-sm ${theme.textMuted}`}>Important updates about your account, events, and registrations</p>
                            </div>
                            <button
                              onClick={() => handleNotificationToggle('email', 'transactional')}
                              className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors ${
                                notificationPreferences.email?.transactional ? 'bg-blue-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                  notificationPreferences.email?.transactional ? 'left-7' : 'left-1'
                                }`}
                              />
                            </button>
                          </div>
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium ${theme.text}`}>Newsletter & Updates</p>
                              <p className={`text-sm ${theme.textMuted}`}>Stay in the loop with product updates, upcoming events, community highlights, and special offers</p>
                            </div>
                            <button
                              onClick={handleNewsletterToggle}
                              className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors ${
                                newsletterSubscribed ? 'bg-blue-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                  newsletterSubscribed ? 'left-7' : 'left-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* SMS Notifications — hidden, coming soon */}

                      {/* Push Notifications */}
                      {isNativePlatform() && (
                        <div className={`p-4 rounded-lg border ${theme.border}`}>
                          <div className="flex items-center mb-4">
                            <Bell className="w-5 h-5 text-blue-600 mr-2" />
                            <h3 className={`font-medium ${theme.text}`}>Push Notifications</h3>
                          </div>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium ${theme.text}`}>Push Notifications</p>
                                <p className={`text-sm ${theme.textMuted}`}>Receive alerts for tournaments, teammates, and important updates</p>
                              </div>
                              <button
                                onClick={handleNativePushToggle}
                                className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors ${
                                  nativePushEnabled ? 'bg-blue-600' : 'bg-gray-300'
                                }`}
                              >
                                <span
                                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                    nativePushEnabled ? 'left-7' : 'left-1'
                                  }`}
                                />
                              </button>
                            </div>
                            {nativePushEnabled && (
                              <div className="flex items-center justify-between gap-4 pt-2 border-t border-dashed">
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm ${theme.textMuted}`}>Send a test notification to verify everything is working</p>
                                </div>
                                <button
                                  onClick={handleTestPush}
                                  disabled={testPushLoading}
                                  className={`${btn.base} ${btn.md} ${theme.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0`}
                                >
                                  {testPushLoading ? 'Sending...' : 'Send Test'}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end pt-6 border-t">
                        <button
                          onClick={handleSaveNotificationPreferences}
                          disabled={loading}
                          className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed flex items-center`}
                        >
                          <Save className="w-5 h-5 mr-2" />
                          {loading ? 'Saving...' : 'Save Preferences'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Security Questions Section */}
              {activeSection === 'security' && (
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <Shield className="w-6 h-6 text-blue-600 mr-3" />
                    <h2 className={`text-xl font-semibold ${theme.text}`}>Security Questions</h2>
                  </div>

                  <p className={`${theme.textMuted} mb-6`}>
                    Security questions help protect your account and can be used to recover your password if you forget it.
                    Choose questions that only you would know the answers to.
                  </p>

                  <div className="space-y-8">
                    {/* Security Question 1 */}
                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-2`}>Security Question 1</label>
                      <select
                        value={securityQuestions.question1}
                        onChange={(e) => handleSecurityQuestionChange('question1', e.target.value)}
                        className={`w-full px-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text} mb-3`}
                      >
                        <option value="">Select a question</option>
                        {securityQuestionOptions.map((question, index) => (
                          <option key={index} value={question}>{question}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={securityQuestions.answer1}
                        onChange={(e) => handleSecurityQuestionChange('answer1', e.target.value)}
                        className={`w-full px-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                        placeholder="Your answer"
                      />
                    </div>

                    {/* Security Question 2 */}
                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-2`}>Security Question 2</label>
                      <select
                        value={securityQuestions.question2}
                        onChange={(e) => handleSecurityQuestionChange('question2', e.target.value)}
                        className={`w-full px-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text} mb-3`}
                      >
                        <option value="">Select a question</option>
                        {securityQuestionOptions.map((question, index) => (
                          <option
                            key={index}
                            value={question}
                            disabled={question === securityQuestions.question1}
                          >
                            {question}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={securityQuestions.answer2}
                        onChange={(e) => handleSecurityQuestionChange('answer2', e.target.value)}
                        className={`w-full px-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                        placeholder="Your answer"
                      />
                    </div>

                    {/* Security Question 3 */}
                    <div>
                      <label className={`block text-sm font-medium ${theme.text} mb-2`}>Security Question 3</label>
                      <select
                        value={securityQuestions.question3}
                        onChange={(e) => handleSecurityQuestionChange('question3', e.target.value)}
                        className={`w-full px-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text} mb-3`}
                      >
                        <option value="">Select a question</option>
                        {securityQuestionOptions.map((question, index) => (
                          <option
                            key={index}
                            value={question}
                            disabled={question === securityQuestions.question1 || question === securityQuestions.question2}
                          >
                            {question}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={securityQuestions.answer3}
                        onChange={(e) => handleSecurityQuestionChange('answer3', e.target.value)}
                        className={`w-full px-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                        placeholder="Your answer"
                      />
                    </div>

                    <div className={`${theme.backgroundSecondary} border ${theme.border} rounded-lg p-4`}>
                      <div className="flex items-start">
                        <Shield className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <h4 className={`font-medium ${theme.text}`}>Security Tips:</h4>
                          <ul className={`text-sm ${theme.textMuted} mt-2 space-y-1`}>
                            <li>• Choose questions with answers that won't change over time</li>
                            <li>• Use answers that only you would know</li>
                            <li>• Don't share your security question answers with anyone</li>
                            <li>• Consider using memorable but not obvious answers</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end pt-6 border-t">
                      <button
                        onClick={handleSaveSecurityQuestions}
                        disabled={loading}
                        className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed flex items-center`}
                      >
                        <Save className="w-5 h-5 mr-2" />
                        {loading ? 'Updating...' : 'Update Security Questions'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Coaching Profile Section */}
              {activeSection === 'coaching' && (
                <div className="p-6">
                  <div className="flex items-center mb-2">
                    <GraduationCap className="w-6 h-6 text-blue-600 mr-3" />
                    <h2 className={`text-xl font-semibold ${theme.text}`}>Coaching Profile</h2>
                  </div>
                  <p className={`text-sm ${theme.textMuted} mb-6`}>
                    Shown on the "Meet Our Coaches" section of any academy where you're listed as a coach.
                  </p>

                  {coachingLoading ? (
                    <div className={`text-sm ${theme.textMuted}`}>Loading...</div>
                  ) : (
                    <div className="space-y-6">
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>Bio</label>
                        <textarea
                          value={coachingBio}
                          onChange={(e) => setCoachingBio(e.target.value)}
                          rows={4}
                          maxLength={1000}
                          className={`w-full px-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text} resize-none`}
                          placeholder="Tell academies and players about your coaching background..."
                        />
                      </div>

                      <div className="max-w-xs">
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>Years of Coaching Experience</label>
                        <input
                          type="number"
                          min="0"
                          max="80"
                          value={coachingYearsExperience}
                          onChange={(e) => setCoachingYearsExperience(e.target.value)}
                          className={`w-full px-4 py-3 ${theme.background} border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${theme.text}`}
                          placeholder="e.g. 8"
                        />
                      </div>

                      {/* Certifications */}
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-2 flex items-center gap-2`}>
                          <Award className="w-4 h-4" /> Certifications
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_1fr_auto] gap-2 mb-3">
                          <input
                            type="text"
                            value={certNameInput}
                            onChange={(e) => setCertNameInput(e.target.value)}
                            placeholder="Certification name"
                            className={`px-3 py-2.5 ${theme.background} border ${theme.border} rounded-lg text-sm ${theme.text}`}
                          />
                          <input
                            type="text"
                            value={certBodyInput}
                            onChange={(e) => setCertBodyInput(e.target.value)}
                            placeholder="Issuing body (optional)"
                            className={`px-3 py-2.5 ${theme.background} border ${theme.border} rounded-lg text-sm ${theme.text}`}
                          />
                          <input
                            type="number"
                            value={certYearInput}
                            onChange={(e) => setCertYearInput(e.target.value)}
                            placeholder="Year"
                            className={`px-3 py-2.5 ${theme.background} border ${theme.border} rounded-lg text-sm ${theme.text}`}
                          />
                          <button
                            type="button"
                            onClick={addCertification}
                            className="px-4 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium flex items-center justify-center gap-1.5"
                          >
                            <Plus size={16} /> Add
                          </button>
                        </div>
                        {certifications.length > 0 && (
                          <div className="space-y-2">
                            {certifications.map((c, i) => (
                              <div key={`${c.name}-${i}`} className={`flex items-center justify-between px-3 py-2 rounded-lg ${theme.backgroundSecondary}`}>
                                <span className={`text-sm ${theme.text}`}>
                                  {c.name}
                                  {c.issuingBody ? ` — ${c.issuingBody}` : ''}
                                  {c.yearObtained ? ` (${c.yearObtained})` : ''}
                                </span>
                                <button type="button" onClick={() => removeCertification(i)} className={theme.textMuted}>
                                  <XIcon size={16} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Per-sport expertise */}
                      <div>
                        <label className={`block text-sm font-medium ${theme.text} mb-2`}>Coaching Expertise by Sport</label>
                        <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_auto] gap-2 mb-3">
                          <select
                            value={expertiseSportId}
                            onChange={(e) => setExpertiseSportId(e.target.value)}
                            className={`px-3 py-2.5 ${theme.background} border ${theme.border} rounded-lg text-sm ${theme.text}`}
                          >
                            <option value="">Select sport</option>
                            {sportTypes.map(s => (
                              <option key={s.id} value={s.id}>{s.sportName}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={expertiseLevelInput}
                            onChange={(e) => setExpertiseLevelInput(e.target.value)}
                            placeholder="Level, e.g. Beginner, U12 specialist"
                            className={`px-3 py-2.5 ${theme.background} border ${theme.border} rounded-lg text-sm ${theme.text}`}
                          />
                          <button
                            type="button"
                            onClick={addExpertise}
                            className="px-4 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium flex items-center justify-center gap-1.5"
                          >
                            <Plus size={16} /> Add
                          </button>
                        </div>
                        {expertise.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {expertise.map((e, i) => (
                              <span key={`${e.sportTypeId}-${i}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium">
                                {e.sportName}{e.level ? `: ${e.level}` : ''}
                                <button type="button" onClick={() => removeExpertise(i)} className="hover:text-blue-900 dark:hover:text-blue-100">
                                  <XIcon size={13} />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end pt-6 border-t">
                        <button
                          onClick={handleSaveCoachingProfile}
                          disabled={coachingSaving}
                          className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed flex items-center`}
                        >
                          <Save className="w-5 h-5 mr-2" />
                          {coachingSaving ? 'Saving...' : 'Save Coaching Profile'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Subscription Section */}
              {activeSection === 'subscription' && (
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <CreditCard className="w-6 h-6 text-blue-600 mr-3" />
                    <h2 className={`text-xl font-semibold ${theme.text}`}>Subscription</h2>
                  </div>
                  {subscriptionLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className={`${theme.backgroundSecondary} rounded-lg p-5`}>
                        <div className="flex items-start justify-between flex-wrap gap-3">
                          <div>
                            <p className={`text-sm ${theme.textSecondary} mb-1`}>Current plan</p>
                            <p className={`text-2xl font-bold ${theme.text}`}>
                              {subscription?.planName || 'Free'}
                            </p>
                            {subscription?.displayPrice && (
                              <p className={`text-sm ${theme.textSecondary} mt-1`}>
                                ${subscription.displayPrice}{subscription.interval ? ` / ${subscription.interval}` : ''}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {subscription?.status && (
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                subscription.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {subscription.isTrial ? 'Trial' : subscription.status}
                              </span>
                            )}
                            {subscription?.isDefault && (
                              <span className={`text-xs ${theme.textMuted}`}>No active subscription</span>
                            )}
                          </div>
                        </div>
                        {subscription?.validUntil && (
                          <p className={`text-sm ${theme.textSecondary} mt-4`}>
                            {subscription.isTrial ? 'Trial ends' : 'Renews'} on{' '}
                            {new Date(subscription.validUntil).toLocaleDateString('en-US', {
                              year: 'numeric', month: 'long', day: 'numeric'
                            })}
                          </p>
                        )}
                      </div>

                      <div className="flex justify-end pt-2">
                        <button
                          onClick={handleManageSubscription}
                          disabled={portalLoading}
                          className={`${btn.base} ${btn.lg} ${theme.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed flex items-center`}
                        >
                          <CreditCard className="w-5 h-5 mr-2" />
                          {portalLoading ? 'Opening...' : 'Manage Subscription'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Delete Account Section (Danger Zone) */}
              {activeSection === 'danger' && (
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <Trash2 className="w-6 h-6 text-red-600 mr-3" />
                    <h2 className={`text-xl font-semibold text-red-600`}>Delete Account</h2>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-red-800">Warning: This action cannot be undone</h4>
                        <ul className="text-sm text-red-700 mt-2 space-y-1">
                          <li>• Your profile will be permanently deleted</li>
                          <li>• Your posts and messages will show as "Deleted User"</li>
                          <li>• You will lose access to all your groups and teams</li>
                          <li>• You will need to create a new account with a different email if you want to return</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {!showDeleteConfirmation ? (
                    <div className="space-y-4">
                      <p className={`${theme.textMuted}`}>
                        If you're sure you want to delete your account, click the button below to proceed.
                      </p>
                      <button
                        onClick={() => setShowDeleteConfirmation(true)}
                        className={`${btn.base} ${btn.lg} ${theme.buttonDanger} flex items-center`}
                      >
                        <Trash2 className="w-5 h-5 mr-2" />
                        I want to delete my account
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className={`font-medium ${theme.text} mb-4`}>
                          To confirm deletion, please enter your current password:
                        </p>
                        <div className="relative max-w-md">
                          <input
                            type={showDeletePassword ? 'text' : 'password'}
                            value={deleteAccountPassword}
                            onChange={(e) => setDeleteAccountPassword(e.target.value)}
                            className={`w-full px-4 py-3 pr-12 ${theme.background} border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${theme.text}`}
                            placeholder="Enter your password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowDeletePassword(!showDeletePassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showDeletePassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>

                      <div className="flex gap-x-4">
                        <button
                          onClick={() => {
                            setShowDeleteConfirmation(false);
                            setDeleteAccountPassword('');
                          }}
                          className={`${btn.base} ${btn.lg} ${theme.buttonSecondary}`}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteAccount}
                          disabled={deletingAccount || !deleteAccountPassword}
                          className={`${btn.base} ${btn.lg} ${theme.buttonDanger} disabled:opacity-50 disabled:cursor-not-allowed flex items-center`}
                        >
                          {deletingAccount ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                              Deleting...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-5 h-5 mr-2" />
                              Permanently Delete My Account
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
      </div>

      {/* Image Crop Modal */}
      <ImageCropModal
        isOpen={cropModalOpen}
        onClose={() => {
          setCropModalOpen(false);
          setSelectedImageForCrop(null);
        }}
        imageSrc={selectedImageForCrop}
        fileType="profile_images"
        onCropComplete={handleCroppedImage}
        shape="round"
        aspectRatio={1}
      />
    </div>
  );
};

export default ProfileEditPage;